import { z } from "zod";
import { UuidSchema, pagedSchema } from "./common.js";
/**
 * Communauté — FORUM + SIGNALEMENTS (bugs/joueurs) : le contrat partagé BFF ↔ web.
 *
 * Tables site `web_forum_*` / `web_report*` (migrations/ensureCommunitySchema.ts côté BFF).
 * SÉCURITÉ (anti-XSS) : tout contenu utilisateur est du TEXTE BRUT — jamais de HTML.
 * `SafeTextSchema` neutralise les caractères de contrôle ; le web rend le contenu via
 * JSX (échappement React) en respectant les sauts de ligne (whitespace-pre-line),
 * JAMAIS via dangerouslySetInnerHTML.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * Texte sûr (anti-XSS / anti-spam de mise en page).
 * ──────────────────────────────────────────────────────────────────────────── */
/** Supprime les caractères de contrôle (sauf \n), normalise \r\n et borne les lignes vides. */
function sanitizePlainText(s) {
    return s
        .replace(/\r\n?/g, "\n")
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
/** Corps de message : texte brut multi-paragraphes (10 à 8000 caractères). */
export const SafeBodySchema = z
    .string()
    .transform(sanitizePlainText)
    .pipe(z.string().min(10, "10 caractères minimum.").max(8000, "8000 caractères maximum."));
/** Réponse courte (dialogue signalement) : 2 à 4000 caractères. */
export const SafeReplySchema = z
    .string()
    .transform(sanitizePlainText)
    .pipe(z.string().min(2, "Message trop court.").max(4000, "4000 caractères maximum."));
/** Titre : une seule ligne, 3 à 150 caractères. */
export const SafeTitleSchema = z
    .string()
    .transform((s) => sanitizePlainText(s).replace(/\n+/g, " "))
    .pipe(z.string().min(3, "3 caractères minimum.").max(150, "150 caractères maximum."));
/* ────────────────────────────────────────────────────────────────────────────
 * FORUM — catégories / sujets / messages.
 * ──────────────────────────────────────────────────────────────────────────── */
/** Slug de catégorie (`annonces`, `discussion-generale`…). */
export const ForumCategoryIdSchema = z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "Slug invalide (minuscules, chiffres, tirets).");
/** Auteur affiché (résolu côté serveur — jamais fourni par le client). */
export const ForumAuthorSchema = z.object({
    accountId: z.number().int().nonnegative(),
    name: z.string(),
    /** uuid MC lié (avatar mc-heads + lien /joueur/:name) — null si compte non lié. */
    uuid: UuidSchema.nullable(),
    /** membre du staff (ADMIN/MODO/STAFF ou super-admin site) → badge sur ses messages. */
    staff: z.boolean(),
});
export const ForumCategorySchema = z.object({
    id: ForumCategoryIdSchema,
    name: z.string(),
    description: z.string(),
    /** glyphe décoratif (sceau de cire du hall). */
    icon: z.string(),
    /** seuls les membres du staff peuvent y OUVRIR des sujets (ex. Annonces). */
    staffOnly: z.boolean(),
    order: z.number().int(),
    threadCount: z.number().int().nonnegative(),
    postCount: z.number().int().nonnegative(),
    /** dernière activité (sujet le plus récemment actif) — null si catégorie vide. */
    lastThread: z
        .object({
        id: z.number().int(),
        title: z.string(),
        lastPostAt: z.number().int(),
        authorName: z.string(),
    })
        .nullable(),
});
export const ForumCategoryListSchema = z.object({ items: z.array(ForumCategorySchema) });
export const ForumThreadSchema = z.object({
    id: z.number().int(),
    categoryId: ForumCategoryIdSchema,
    title: z.string(),
    author: ForumAuthorSchema,
    pinned: z.boolean(),
    locked: z.boolean(),
    views: z.number().int().nonnegative(),
    /** nombre de RÉPONSES (messages - 1, jamais négatif). */
    replyCount: z.number().int().nonnegative(),
    createdAt: z.number().int(),
    lastPostAt: z.number().int(),
    lastPostAuthorName: z.string().nullable(),
});
export const ForumThreadPageSchema = pagedSchema(ForumThreadSchema);
export const ForumPostSchema = z.object({
    id: z.number().int(),
    threadId: z.number().int(),
    author: ForumAuthorSchema,
    /** TEXTE BRUT (rendu échappé côté web) ; vide si le message est supprimé. */
    content: z.string(),
    createdAt: z.number().int(),
    editedAt: z.number().int().nullable(),
    /** suppression douce : le squelette du message reste visible (« message supprimé »). */
    deleted: z.boolean(),
});
export const ForumPostPageSchema = pagedSchema(ForumPostSchema);
/** Vue d'un sujet : le sujet + sa catégorie + une page de messages. */
export const ForumThreadViewSchema = z.object({
    thread: ForumThreadSchema,
    category: ForumCategorySchema,
    posts: ForumPostPageSchema,
});
/* — Requêtes forum (côté joueur) — */
export const ForumThreadCreateReqSchema = z.object({
    categoryId: ForumCategoryIdSchema,
    title: SafeTitleSchema,
    content: SafeBodySchema,
});
export const ForumPostCreateReqSchema = z.object({
    content: SafeBodySchema,
});
export const ForumPostEditReqSchema = z.object({
    content: SafeBodySchema,
});
/* — Requêtes forum (modération staff) — */
export const ForumThreadModReqSchema = z
    .object({
    pinned: z.boolean().optional(),
    locked: z.boolean().optional(),
    /** déplacer le sujet vers une autre catégorie. */
    categoryId: ForumCategoryIdSchema.optional(),
})
    .refine((v) => v.pinned !== undefined || v.locked !== undefined || v.categoryId !== undefined, {
    message: "Aucune modification demandée.",
});
export const ForumCategoryUpsertReqSchema = z.object({
    name: z.string().min(2).max(80),
    description: z.string().max(255).default(""),
    icon: z.string().max(8).default("✦"),
    staffOnly: z.boolean().default(false),
    order: z.number().int().min(0).max(999).default(0),
});
/* ────────────────────────────────────────────────────────────────────────────
 * SIGNALEMENTS — bugs / joueurs / autres, avec dialogue staff ↔ auteur.
 * ──────────────────────────────────────────────────────────────────────────── */
export const REPORT_TYPES = ["bug", "joueur", "autre"];
export const ReportTypeSchema = z.enum(REPORT_TYPES);
export const REPORT_SEVERITIES = ["basse", "normale", "haute", "critique"];
export const ReportSeveritySchema = z.enum(REPORT_SEVERITIES);
export const REPORT_STATUSES = ["nouveau", "en_cours", "resolu", "rejete"];
export const ReportStatusSchema = z.enum(REPORT_STATUSES);
/** Libellés FR d'affichage (web public + admin). */
export const REPORT_TYPE_LABEL_FR = {
    bug: "Bug",
    joueur: "Joueur",
    autre: "Autre",
};
export const REPORT_SEVERITY_LABEL_FR = {
    basse: "Basse",
    normale: "Normale",
    haute: "Haute",
    critique: "Critique",
};
export const REPORT_STATUS_LABEL_FR = {
    nouveau: "Nouveau",
    en_cours: "En cours",
    resolu: "Résolu",
    rejete: "Rejeté",
};
export const ReportSchema = z.object({
    id: z.number().int(),
    type: ReportTypeSchema,
    title: z.string(),
    description: z.string(),
    /** étapes de reproduction (gabarit bug) — null si non renseignées. */
    steps: z.string().nullable(),
    severity: ReportSeveritySchema,
    status: ReportStatusSchema,
    author: ForumAuthorSchema,
    /** membre du staff assigné (null si file d'attente). */
    assigneeName: z.string().nullable(),
    /** note de résolution posée au changement de statut — visible par l'auteur. */
    resolution: z.string().nullable(),
    replyCount: z.number().int().nonnegative(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
});
export const ReportPageSchema = pagedSchema(ReportSchema);
export const ReportReplySchema = z.object({
    id: z.number().int(),
    reportId: z.number().int(),
    authorName: z.string(),
    /** true si réponse du staff (mise en avant côté auteur). */
    staff: z.boolean(),
    content: z.string(),
    createdAt: z.number().int(),
});
/** Détail d'un signalement : le ticket + son dialogue. */
export const ReportViewSchema = z.object({
    report: ReportSchema,
    replies: z.array(ReportReplySchema),
});
/* — Requêtes signalements — */
export const ReportCreateReqSchema = z.object({
    type: ReportTypeSchema,
    title: SafeTitleSchema,
    description: SafeBodySchema,
    /** étapes de reproduction (facultatif, gabarit bug). */
    steps: z
        .string()
        .transform(sanitizePlainText)
        .pipe(z.string().max(4000, "4000 caractères maximum."))
        .optional(),
    severity: ReportSeveritySchema.default("normale"),
});
export const ReportReplyReqSchema = z.object({
    content: SafeReplySchema,
});
/** Mutation staff : transition de statut / assignation / note / sévérité. */
export const ReportUpdateReqSchema = z
    .object({
    status: ReportStatusSchema.optional(),
    /** note de résolution (exigée par le web pour resolu/rejete ; visible par l'auteur). */
    resolution: z
        .string()
        .transform(sanitizePlainText)
        .pipe(z.string().max(4000))
        .optional(),
    severity: ReportSeveritySchema.optional(),
    /** true = s'assigner soi-même ; false = désassigner. */
    assignToMe: z.boolean().optional(),
})
    .refine((v) => v.status !== undefined ||
    v.resolution !== undefined ||
    v.severity !== undefined ||
    v.assignToMe !== undefined, { message: "Aucune modification demandée." });
/** Compteurs de file (colonnes du tableau admin + pastilles live publiques). */
export const ReportQueueStatsSchema = z.object({
    nouveau: z.number().int().nonnegative(),
    en_cours: z.number().int().nonnegative(),
    resolu: z.number().int().nonnegative(),
    rejete: z.number().int().nonnegative(),
});
/** Statistiques publiques de la communauté (hall du forum + hero signalements). */
export const CommunityStatsSchema = z.object({
    threads: z.number().int().nonnegative(),
    posts: z.number().int().nonnegative(),
    /** signalements résolus (affiché en « bugs forgés » sur le hero). */
    reportsResolved: z.number().int().nonnegative(),
    reportsOpen: z.number().int().nonnegative(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * SUGGESTIONS — tableau d'idées VOTÉ (upvote / downvote), avec verdict du staff.
 *
 * Un vrai « suggestions board » façon feature-request : chaque idée porte un
 * score (Σ des votes ∈ {-1,+1}), une catégorie, un statut de traitement scellé
 * par le staff (avec note obligatoire pour les états terminaux). Contenu TEXTE
 * BRUT — même contrat anti-XSS que le forum (rendu échappé côté web).
 * Tables site `web_suggestions` / `web_suggestion_votes`.
 * ──────────────────────────────────────────────────────────────────────────── */
export const SUGGESTION_CATEGORIES = ["gameplay", "economie", "donjons", "site", "autre"];
export const SuggestionCategorySchema = z.enum(SUGGESTION_CATEGORIES);
export const SUGGESTION_STATUSES = [
    "proposee",
    "a_etude",
    "acceptee",
    "refusee",
    "implementee",
];
export const SuggestionStatusSchema = z.enum(SUGGESTION_STATUSES);
/** Tris publics du tableau (populaires = score, récentes = date, statut = groupé). */
export const SUGGESTION_SORTS = ["populaires", "recentes", "statut"];
export const SuggestionSortSchema = z.enum(SUGGESTION_SORTS);
/** Libellés FR d'affichage (web public + admin). */
export const SUGGESTION_CATEGORY_LABEL_FR = {
    gameplay: "Gameplay",
    economie: "Économie",
    donjons: "Donjons",
    site: "Site & Web",
    autre: "Autre",
};
export const SUGGESTION_STATUS_LABEL_FR = {
    proposee: "Proposée",
    a_etude: "À l'étude",
    acceptee: "Acceptée",
    refusee: "Refusée",
    implementee: "Implémentée",
};
/** États terminaux : la note du staff (verdict) y est EXIGÉE. */
export const SUGGESTION_TERMINAL_STATUSES = [
    "acceptee",
    "refusee",
    "implementee",
];
export const SuggestionSchema = z.object({
    id: z.number().int(),
    title: z.string(),
    /** TEXTE BRUT (rendu échappé côté web). */
    body: z.string(),
    category: SuggestionCategorySchema,
    status: SuggestionStatusSchema,
    author: ForumAuthorSchema,
    /** score net (Σ votes) — cache dé-normalisé, recalculé à chaque vote. */
    score: z.number().int(),
    /** vote du visiteur courant sur cette idée : -1, 0 (aucun) ou +1. */
    myVote: z.number().int().min(-1).max(1),
    /** note / verdict du staff — visible publiquement quand le statut est scellé (null sinon). */
    staffNote: z.string().nullable(),
    /** membre du staff en charge (null = file d'attente). */
    assigneeName: z.string().nullable(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
});
export const SuggestionPageSchema = pagedSchema(SuggestionSchema);
/** Compteurs par statut (tuiles-filtres du tableau public + file admin). */
export const SuggestionStatusCountsSchema = z.object({
    proposee: z.number().int().nonnegative(),
    a_etude: z.number().int().nonnegative(),
    acceptee: z.number().int().nonnegative(),
    refusee: z.number().int().nonnegative(),
    implementee: z.number().int().nonnegative(),
});
/* — Requêtes suggestions (côté joueur) — */
export const SuggestionCreateReqSchema = z.object({
    title: SafeTitleSchema,
    category: SuggestionCategorySchema,
    body: SafeBodySchema,
});
/** Vote : +1 (upvote), -1 (downvote) ou 0 (retirer son vote). */
export const SuggestionVoteReqSchema = z.object({
    value: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
});
/* — Requêtes suggestions (traitement staff) — */
/** Mutation staff : transition de statut + note (exigée pour états terminaux) + assignation. */
export const SuggestionUpdateReqSchema = z
    .object({
    status: SuggestionStatusSchema.optional(),
    /** note / verdict (exigée par le web pour acceptee/refusee/implementee ; visible publiquement). */
    staffNote: z
        .string()
        .transform(sanitizePlainText)
        .pipe(z.string().max(4000))
        .optional(),
    /** true = s'assigner soi-même ; false = désassigner. */
    assignToMe: z.boolean().optional(),
})
    .refine((v) => v.status !== undefined || v.staffNote !== undefined || v.assignToMe !== undefined, { message: "Aucune modification demandée." });
//# sourceMappingURL=community.js.map